import { DateTime } from 'luxon';
import { brandStyleVars } from '@moxie/branding';

const tokenValue = (value) => (value == null ? '' : String(value));

const putCustomValues = (tokens, prefix, values) => {
	(values || []).forEach((field) => {
		if (!field || !field.mappingKey) return;
		const key = `${prefix}.Custom.${field.mappingKey}`;
		const raw = tokenValue(field.value);
		const date = field.type === 'Date' && raw ? DateTime.fromISO(raw) : null;
		tokens.set(key, date && date.isValid ? date.toLocaleString(DateTime.DATE_FULL) : raw);
		if (date && date.isValid) tokens.set(`${key}.ISO`, raw);
	});
};

export const buildCustomPageTokens = (context) => {
	const tokens = new Map();
	const client = context && context.client;
	const contact = context && context.contact;

	if (client) {
		[
			['Client.Name', client.name],
			['Client.Initials', client.initials],
			['Client.Address1', client.address1],
			['Client.Address2', client.address2],
			['Client.City', client.city],
			['Client.Locality', client.locality],
			['Client.Postal', client.postal],
			['Client.Country', client.country],
			['Client.Phone', client.phone],
			['Client.Website', client.website],
			['Client.TaxId', client.taxId],
			['Client.Color', client.color],
			['Client.Logo', client.logo],
			['Client.RecordType', client.clientType],
		].forEach(([key, value]) => tokens.set(key, tokenValue(value)));
		putCustomValues(tokens, 'Client', client.customValues);
	}

	if (contact) {
		[
			['Contact.First', contact.firstName],
			['Contact.Last', contact.lastName],
			['Contact.FullName', contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim()],
			['Contact.Email', contact.email],
			['Contact.Phone', contact.phone],
			['Contact.Mobile', contact.mobile],
			['Contact.Role', contact.role],
		].forEach(([key, value]) => tokens.set(key, tokenValue(value)));
		putCustomValues(tokens, 'Contact', contact.customValues);
	}

	return tokens;
};

const replaceTokens = (value, tokens) =>
	String(value || '').replace(/{{\s*([A-Za-z0-9_.-]+)\s*}}/g, (match, name) =>
		tokens.has(name) ? tokens.get(name) : match
	);

const isUnsafeUrl = (value) => /^(?:javascript|vbscript|data\s*:\s*text\/html)\s*:/i.test(String(value || '').trim());

// Resolve text and ordinary content attributes through the DOM rather than
// concatenating strings. Client/contact values therefore remain text and
// cannot inject markup into a page authored by the workspace owner.
export const renderCustomPageContent = (html, context, branding) => {
	if (!html) return '';
	const tokens = buildCustomPageTokens(context);
	if (!tokens.size && !branding) return html;

	const container = document.createElement('div');
	container.innerHTML = html;
	// Saved kit variables are a portable fallback; portal pages follow the current
	// publication. Leave authored layout and descendant formatting untouched.
	if (branding) {
		container.querySelectorAll('[data-mx-root][data-mx-builder="contentbuilder-1"]').forEach((root) => {
			for (const name of Array.from(root.style)) {
				if (name.startsWith('--brand-')) root.style.removeProperty(name);
			}
			Object.entries(brandStyleVars(branding)).forEach(([name, value]) => root.style.setProperty(name, value));
		});
	}
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
	const textNodes = [];
	while (walker.nextNode()) textNodes.push(walker.currentNode);
	textNodes.forEach((node) => {
		if (['STYLE', 'SCRIPT'].includes(node.parentElement && node.parentElement.tagName)) return;
		node.nodeValue = replaceTokens(node.nodeValue, tokens);
	});

	container.querySelectorAll('*').forEach((element) => {
		['title', 'aria-label', 'alt', 'href', 'src'].forEach((attribute) => {
			if (!element.hasAttribute(attribute)) return;
			const resolved = replaceTokens(element.getAttribute(attribute), tokens);
			element.setAttribute(attribute, ['href', 'src'].includes(attribute) && isUnsafeUrl(resolved) ? '' : resolved);
		});
	});

	return container.innerHTML;
};
